import { RefObject, useCallback } from 'react'

import { useQueryClient } from '@tanstack/react-query'

type UseIframeRefsQueryReturnType = (
  callbackFn: (iframes: IframeRefType[]) => IframeRefType[],
) => void

export interface IframeRefType {
  id: string
  ref?: RefObject<HTMLIFrameElement | null>
  url?: string
  containerRef?: RefObject<HTMLDivElement | undefined>
  callback?: () => void
}

type UseIFrameRefsQueryType = () => {
  addIframe: (iframe: IframeRefType) => void
  removeIframe: (id: IframeRefType['id']) => void
  getIframesByIdOrUrl: (id: string) => IframeRefType[] | undefined
}

const isTheSameIframeInstance = (
  id: IframeRefType['id'],
  otherId: IframeRefType['id'],
) => id === otherId

const useIframeRefs: UseIFrameRefsQueryType = () => {
  const queryClient = useQueryClient()

  const setIframeRefs = useCallback<UseIframeRefsQueryReturnType>(
    (callbackFn) => {
      const currentIframeRefs =
        queryClient.getQueryData<IframeRefType[]>(['iframe-refs']) ?? []

      const newIframeRefs = callbackFn(currentIframeRefs)

      queryClient.setQueryData(['iframe-refs'], newIframeRefs)
      queryClient.invalidateQueries({ queryKey: ['iframe-refs'], exact: true })
    },
    [queryClient],
  )

  const addIframe = useCallback(
    (iframe: IframeRefType) => {
      setIframeRefs((currentIframeRefs) => {
        const iframeWithSameId = currentIframeRefs.find((currentIframeRef) =>
          isTheSameIframeInstance(currentIframeRef.id, iframe.id),
        )
        // check if the iframe already exists in the list and do not add it again
        return iframeWithSameId
          ? currentIframeRefs
          : [...currentIframeRefs, iframe]
      })
    },
    [setIframeRefs],
  )

  // In case of plain embedded content, the id of the iframe is a nanoid
  // For all the other use cases, the id is the iframe url
  const getIframesByIdOrUrl = useCallback(
    (id: string) => {
      const iframeRefs = queryClient.getQueryData<IframeRefType[]>([
        'iframe-refs',
      ])
      return iframeRefs?.filter(
        (iframeRef) => iframeRef.id === id || iframeRef.url === id,
      )
    },
    [queryClient],
  )

  const removeIframe = useCallback(
    (id: IframeRefType['id']) =>
      setIframeRefs((currentIframeRefs) =>
        currentIframeRefs.filter(
          (iframe) => !isTheSameIframeInstance(iframe.id, id),
        ),
      ),
    [setIframeRefs],
  )

  return {
    addIframe,
    removeIframe,
    getIframesByIdOrUrl,
  }
}

export default useIframeRefs
